import { ArrowRight, Download, ExternalLink } from 'lucide-react'
import React from 'react'
import { Link } from 'react-router-dom'
import { SEOHead } from '../components/seo/SEOHead'
import { Button, Card, Container, Heading, Text } from '../components/ui'

/**
 * Professional homepage serving as the main landing page for Lucas Stufflebeam's portfolio.
 * Features a hero section, key achievements, and clear navigation to portfolio sections.
 */
const HomePage: React.FC = () => {
  const handleDownloadResume = () => {
    const link = document.createElement('a')
    link.href =
      '/Lucas Stufflebeam-Principal Software Architect-Resume 2025.pdf'
    link.download =
      'Lucas Stufflebeam-Principal Software Architect-Resume 2025.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <SEOHead page="home" path="/" />

      <main className="page-main" role="main">
        <Container size="lg" padding="lg">
          {/* Hero Section */}
          <section className="hero-section">
            <div className="hero-content">
              <Heading
                as="h1"
                size="4xl"
                variant="gradient"
                className="hero-title"
              >
                Lucas Stufflebeam
              </Heading>
              <Text size="xl" variant="accent" className="hero-subtitle">
                Principal Software Architect
              </Text>
              <Text size="lg" variant="secondary" className="hero-description">
                15+ years architecting scalable, high-performance systems for
                global applications. Expert in cloud-native architectures,
                Kubernetes, and modern DevOps practices.
              </Text>

              <div className="hero-actions">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleDownloadResume}
                  className="hero-cta"
                >
                  <Download size={20} />
                  Download Resume
                </Button>
                <Link to="/projects" className="button-link">
                  <Button
                    variant="secondary"
                    size="lg"
                    className="hero-secondary"
                  >
                    View Projects
                    <ArrowRight size={20} />
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Key Achievements */}
          <section className="achievements-preview">
            <Heading as="h2" size="2xl" className="section-title">
              Key Achievements
            </Heading>

            <div className="achievements-grid">
              <Card variant="glass" padding="lg" className="achievement-card">
                <Heading as="h3" size="lg" variant="secondary">
                  100,000+ Concurrent Users
                </Heading>
                <Text variant="secondary">
                  Architected and scaled global backend platform supporting
                  massive concurrent load across 7 live products with 99.9%
                  uptime.
                </Text>
              </Card>

              <Card variant="glass" padding="lg" className="achievement-card">
                <Heading as="h3" size="lg" variant="secondary">
                  40% Cost Reduction
                </Heading>
                <Text variant="secondary">
                  Implemented cloud optimization strategies that reduced
                  infrastructure costs while maintaining performance during
                  sustained growth.
                </Text>
              </Card>

              <Card variant="glass" padding="lg" className="achievement-card">
                <Heading as="h3" size="lg" variant="secondary">
                  22 Shipped Games
                </Heading>
                <Text variant="secondary">
                  Credited on 22 successful game launches, serving millions of
                  players worldwide.
                </Text>
              </Card>
            </div>
          </section>

          {/* Portfolio Navigation */}
          <section className="portfolio-navigation">
            <Heading as="h2" size="2xl" className="section-title">
              Explore My Work
            </Heading>

            <div className="nav-cards">
              <Card variant="surface" padding="lg" className="nav-card">
                <Heading as="h3" size="lg">
                  About Me
                </Heading>
                <Text variant="secondary">
                  Learn about my technical philosophy, career journey, and
                  expertise in software architecture and cloud technologies.
                </Text>
                <Link to="/about" className="button-link">
                  <Button variant="ghost" size="sm">
                    Read More <ArrowRight size={16} />
                  </Button>
                </Link>
              </Card>

              <Card variant="surface" padding="lg" className="nav-card">
                <Heading as="h3" size="lg">
                  Project Portfolio
                </Heading>
                <Text variant="secondary">
                  Explore a selection of my 36+ projects across game
                  development, enterprise systems, and cloud architecture with
                  detailed technical insights.
                </Text>
                <Link to="/projects" className="button-link">
                  <Button variant="ghost" size="sm">
                    View Projects <ArrowRight size={16} />
                  </Button>
                </Link>
              </Card>

              <Card variant="surface" padding="lg" className="nav-card">
                <Heading as="h3" size="lg">
                  Case Studies
                </Heading>
                <Text variant="secondary">
                  Deep technical analysis of complex systems architecture
                  challenges with measurable business impact and lessons
                  learned.
                </Text>
                <Link to="/case-studies" className="button-link">
                  <Button variant="ghost" size="sm">
                    View Case Studies <ArrowRight size={16} />
                  </Button>
                </Link>
              </Card>

              <Card variant="surface" padding="lg" className="nav-card">
                <Heading as="h3" size="lg">
                  Professional Resume
                </Heading>
                <Text variant="secondary">
                  Comprehensive professional profile with detailed experience,
                  skills, and achievements. Available in PDF format.
                </Text>
                <Link to="/resume" className="button-link">
                  <Button variant="ghost" size="sm">
                    View Resume <ArrowRight size={16} />
                  </Button>
                </Link>
              </Card>
            </div>
          </section>

          {/* Contact CTA */}
          <section className="contact-cta">
            <Card variant="glass" padding="lg" className="cta-card">
              <Heading as="h2" size="xl" className="cta-title">
                Ready to Build Something Amazing?
              </Heading>
              <Text size="lg" variant="primary" className="cta-description">
                I'm available for principal architect roles, technical
                leadership positions, and challenging software architecture
                projects.
              </Text>
              <div className="cta-actions">
                <a
                  href="mailto:lucas@lucasstufflebeam.com"
                  className="button-link"
                >
                  <Button variant="primary" size="lg">
                    Get In Touch
                  </Button>
                </a>
                <a
                  href="https://linkedin.com/in/lstufflebeam"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button-link"
                >
                  <Button variant="secondary" size="lg">
                    LinkedIn <ExternalLink size={16} />
                  </Button>
                </a>
              </div>
            </Card>
          </section>
        </Container>
      </main>
    </>
  )
}

export default HomePage
